import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ClipboardCheck, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { todayLocal } from "@/lib/toolbox";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/toolbox/assignments")({
  component: ToolboxAssignmentsPage,
});

type State = "waiting" | "started" | "done" | "cancelled";

interface Assignment {
  id: string;
  title: string;
  dueDate: string | null;
  note: string | null;
  assignedByName: string | null;
  mine: boolean;
  canCancel: boolean;
  cancelledAt: string | null;
  cancelledByName: string | null;
  cancelReason: string | null;
  targets: { targetId: string; site: string | null; foreman: string | null; meetingId: string | null; state: State; canCancel: boolean }[];
}

const STATE = {
  waiting: { label: "Not started", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  started: { label: "Started", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  done: { label: "Done", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground line-through" },
} as const;

/** What the person is about to cancel: the whole assignment, or one site. */
type CancelAsk = { id: string; title: string; target?: { targetId: string; site: string | null; foreman: string | null } };

/** Toolboxes handed to sites, and where each foreman stands (super admins and division admins). */
function ToolboxAssignmentsPage() {
  const goBack = useGoBack({ to: "/toolbox" });
  const qc = useQueryClient();
  const [ask, setAsk] = useState<CancelAsk | null>(null);
  const [reason, setReason] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["toolbox", "assignments"],
    queryFn: () => apiGet<{ data: Assignment[] }>("/api/toolbox/assignments"),
    refetchInterval: 30_000,
  });
  const { data: cancelledData } = useQuery({
    queryKey: ["toolbox", "assignments", "cancelled"],
    queryFn: () => apiGet<{ data: Assignment[] }>("/api/toolbox/assignments?view=cancelled"),
    enabled: !isError,
  });
  const cancel = useMutation({
    mutationFn: (a: CancelAsk) =>
      apiPost(a.target ? `/api/toolbox/assignments/${a.id}/targets/${a.target.targetId}/cancel` : `/api/toolbox/assignments/${a.id}/cancel`, { reason }),
    onSuccess: (_r, a) => {
      toast.success(a.target ? `Taken back for ${a.target.site ?? "that site"}. The foreman was told.` : "Assignment taken back. The foremen were told.");
      setAsk(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["toolbox"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const all = data?.data ?? [];
  // Like the other assignment lists: finished ones (nothing left to hold) go to a Completed list below
  const isOpen = (a: Assignment) => a.targets.some((t) => t.state === "waiting" || t.state === "started");
  const rows = all.filter(isOpen);
  const doneRows = all.filter((a) => !isOpen(a));
  const cancelled = cancelledData?.data ?? [];
  const today = todayLocal();

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-10">
      <div className="flex items-center gap-2 pt-2">
        <button type="button" onClick={goBack} className="rounded p-1 hover:bg-muted" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-xl font-bold text-text-primary">Assigned toolboxes</h1>
      </div>
      <p className="text-sm text-muted-foreground">Toolboxes you handed to sites. To assign one, open the Toolbox library and press Assign on a prepared talk or a held toolbox. You can take one back for a single site or for all of them.</p>

      {isLoading && <Skeleton className="h-24 w-full rounded-xl" />}
      {isError && <p className="text-sm text-destructive">This page is for super admins and division admins.</p>}
      {!isLoading && !isError && all.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <ClipboardCheck className="mb-2 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nothing assigned right now.</p>
            <Link to="/toolbox/library" search={{}} className="mt-2 text-sm text-paris-red-text underline underline-offset-2">Open the Toolbox library</Link>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && all.length > 0 && rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing is waiting. Finished ones are in Completed below.</p>}

      {rows.map((a) => {
        const overdue = !!a.dueDate && a.dueDate < today && a.targets.some((t) => t.state === "waiting" || t.state === "started");
        return (
          <Card key={a.id}>
            <CardContent className="space-y-2 px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{a.title}</p>
                  <p className={cn("text-xs", overdue ? "font-medium text-red-600" : "text-muted-foreground")}>
                    {a.dueDate ? `Due ${formatDateOnly(a.dueDate)}${overdue ? " (overdue)" : ""}` : "No due date"}
                    {a.assignedByName ? ` · by ${a.assignedByName}` : ""}
                  </p>
                  {a.note && <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{a.note}</p>}
                </div>
                {a.canCancel && (
                  <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1 text-xs text-destructive" onClick={() => { setReason(""); setAsk({ id: a.id, title: a.title }); }}>
                    <Ban className="h-3.5 w-3.5" /> Cancel all
                  </Button>
                )}
              </div>
              <ul className="divide-y rounded-lg border">
                {a.targets.map((t) => (
                  <li key={t.targetId} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className={cn("min-w-0", t.state === "cancelled" && "opacity-60")}>
                      <span className="block truncate font-medium">{t.site}</span>
                      <span className="block truncate text-xs text-muted-foreground">{t.foreman}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {t.meetingId ? (
                        <Link to="/toolbox/$meetingId" params={{ meetingId: t.meetingId }} className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATE[t.state].cls)}>{STATE[t.state].label}</Link>
                      ) : (
                        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATE[t.state].cls)}>{STATE[t.state].label}</span>
                      )}
                      {t.canCancel && (
                        <button
                          type="button"
                          className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10"
                          aria-label={`Cancel for ${t.site ?? "this site"}`}
                          onClick={() => { setReason(""); setAsk({ id: a.id, title: a.title, target: { targetId: t.targetId, site: t.site, foreman: t.foreman } }); }}
                        >
                          Cancel
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}

      {doneRows.length > 0 && (
        <section className="space-y-2 pt-2">
          <button type="button" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)} className="flex w-full items-center justify-between text-left">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completed ({doneRows.length})</span>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showDone && "rotate-180")} />
          </button>
          {showDone &&
            doneRows.map((a) => (
              <Card key={a.id} className="opacity-90">
                <CardContent className="space-y-1.5 px-4 py-3">
                  <p className="text-sm font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.dueDate ? `Was due ${formatDateOnly(a.dueDate)}` : "No due date"}
                    {a.assignedByName ? ` · by ${a.assignedByName}` : ""}
                  </p>
                  <ul className="divide-y rounded-lg border">
                    {a.targets.map((t) => (
                      <li key={t.targetId} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className={cn("min-w-0", t.state === "cancelled" && "opacity-60")}>
                          <span className="block truncate font-medium">{t.site}</span>
                          <span className="block truncate text-xs text-muted-foreground">{t.foreman}</span>
                        </span>
                        {t.meetingId ? (
                          <Link to="/toolbox/$meetingId" params={{ meetingId: t.meetingId }} className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", STATE[t.state].cls)}>{STATE[t.state].label}</Link>
                        ) : (
                          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", STATE[t.state].cls)}>{STATE[t.state].label}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
        </section>
      )}

      {cancelled.length > 0 && (
        <section className="space-y-2 pt-2">
          <button type="button" aria-expanded={showCancelled} onClick={() => setShowCancelled((v) => !v)} className="flex w-full items-center justify-between text-left">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cancelled ({cancelled.length})</span>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showCancelled && "rotate-180")} />
          </button>
          {showCancelled &&
            cancelled.map((a) => (
              <Card key={a.id} className="opacity-80">
                <CardContent className="space-y-1 px-4 py-3">
                  <p className="text-sm font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground">
                    Cancelled {a.cancelledAt ? formatDateOnly(a.cancelledAt.slice(0, 10)) : ""}
                    {a.cancelledByName ? ` by ${a.cancelledByName}` : ""}
                  </p>
                  {a.cancelReason && <p className="whitespace-pre-line text-xs">Reason: {a.cancelReason}</p>}
                  <p className="text-xs text-muted-foreground">
                    {a.targets.map((t) => `${t.site ?? "Site"}${t.state === "done" ? " (was done)" : ""}`).join(" · ")}
                  </p>
                </CardContent>
              </Card>
            ))}
        </section>
      )}

      <Dialog open={!!ask} onOpenChange={(o) => !o && !cancel.isPending && setAsk(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>{ask?.target ? "Take it back for this site?" : "Cancel this assignment?"}</DialogTitle>
          <DialogDescription>
            {ask?.target
              ? `"${ask.title}" is no longer asked of ${ask.target.foreman ?? "the foreman"} at ${ask.target.site ?? "this site"}. They are told.`
              : `"${ask?.title}" is no longer asked of any foreman who has not finished it. They are told. It moves to the Cancelled list below.`}
          </DialogDescription>
          <div className="space-y-1.5 pt-2">
            <label htmlFor="cancel-reason" className="text-sm font-medium">Reason (optional)</label>
            <Textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder="Example: The pour was moved to next week." className="placeholder:italic placeholder:text-muted-foreground/50" />
          </div>
          <div className="flex flex-col-reverse gap-2 pt-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" disabled={cancel.isPending} onClick={() => setAsk(null)}>Keep it</Button>
            <Button type="button" variant="destructive" disabled={cancel.isPending} onClick={() => ask && cancel.mutate(ask)}>
              {cancel.isPending ? "Cancelling..." : ask?.target ? "Cancel for this site" : "Cancel assignment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
